import { useEffect, useRef, useState, useCallback } from 'react';
import SignaturePadLib from 'signature_pad';
import { RotateCcw, PenLine } from 'lucide-react';

interface SignaturePadProps {
  onSign: (dataUrl: string) => void;
  disabled?: boolean;
}

export function SignaturePad({ onSign, disabled = false }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const padRef = useRef<SignaturePadLib | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [confirmed, setConfirmed] = useState(false);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !padRef.current) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(ratio, ratio);
    padRef.current.clear();
    setIsEmpty(true);
    setConfirmed(false);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const pad = new SignaturePadLib(canvas, {
      backgroundColor: 'rgba(255,255,255,0)',
      penColor: '#1a1a2e',
      minWidth: 1.5,
      maxWidth: 3,
    });

    pad.addEventListener('beginStroke', () => {
      setIsEmpty(false);
      setConfirmed(false);
    });

    padRef.current = pad;
    resizeCanvas();

    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(canvas.parentElement!);

    return () => {
      observer.disconnect();
      pad.off();
    };
  }, [resizeCanvas]);

  const handleClear = () => {
    padRef.current?.clear();
    setIsEmpty(true);
    setConfirmed(false);
  };

  const handleConfirm = () => {
    if (!padRef.current || padRef.current.isEmpty()) return;
    const dataUrl = padRef.current.toDataURL('image/png');
    onSign(dataUrl);
    setConfirmed(true);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="relative rounded-xl border-2 border-dashed border-gray-300 bg-white overflow-hidden"
           style={{ height: 160 }}>
        {isEmpty && !confirmed && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-gray-400 gap-1">
            <PenLine className="w-6 h-6" />
            <span className="text-sm">Signez ici</span>
          </div>
        )}
        {confirmed && (
          <div className="absolute top-2 right-2 bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full font-medium">
            Confirmée
          </div>
        )}
        <canvas
          ref={canvasRef}
          className="w-full h-full touch-none"
          style={{ cursor: disabled ? 'not-allowed' : 'crosshair' }}
        />
        {disabled && (
          <div className="absolute inset-0 bg-gray-100/60 rounded-xl" />
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled || isEmpty}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Effacer
        </button>

        <button
          type="button"
          onClick={handleConfirm}
          disabled={disabled || isEmpty || confirmed}
          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <PenLine className="w-4 h-4" />
          {confirmed ? 'Signature confirmée' : 'Valider ma signature'}
        </button>
      </div>
    </div>
  );
}

export default SignaturePad;
